// apps/accounting-frontend/src/pages/TaxReportPage.tsx
// 稅務報表頁面 - 顯示營業稅申報書 (401/403) 和稅務統計

import React, { useEffect, useRef } from 'react';
import { Button, CustomSelect } from '@redpanda/ui';
import { useTaxReportStore } from '../stores/taxReportStore';
import { useTranslation } from 'react-i18next';

const TaxReportPage: React.FC = () => {
  const { t } = useTranslation();
  const reportContentRef = useRef<HTMLDivElement>(null);

  const {
    availablePeriods,
    selectedPeriod,
    periodDataCache,
    isLoading,
    error,
    fetchTaxPeriods,
    setSelectedPeriod,
    fetchTaxReportData
  } = useTaxReportStore();

  // 獲取當前期間的快取數據
  const currentData = selectedPeriod ? periodDataCache[selectedPeriod]?.data : null;
  const report401 = currentData?.report401;
  const report403 = currentData?.report403;
  const taxSummary = currentData?.taxSummary;

  // 載入可用的申報期間
  useEffect(() => {
    if (availablePeriods.length === 0) {
      fetchTaxPeriods();
    }
  }, []);

  // 當選擇期間變更時，載入稅務資料
  useEffect(() => {
    if (selectedPeriod) {
      fetchTaxReportData(selectedPeriod);
    }
  }, [selectedPeriod]);

  const formatAmount = (amount: number): string => {
    return `$${amount.toLocaleString()}`;
  };

  const formatPeriodDisplay = (period: string): string => {
    const [year, month] = period.split('-');
    return `${year}/${parseInt(month)}`;
  };

  // PDF 匯出功能
  const handleExportPDF = async () => {
    // 動態載入 jsPDF 和 html2canvas
    const [jsPDF, html2canvas] = await Promise.all([
      import('jspdf').then(mod => mod.default),
      import('html2canvas').then(mod => mod.default)
    ]);

    if (!reportContentRef.current) return;

    try {
      // 檢查是否為深色模式
      const isDarkMode = document.documentElement.classList.contains('dark');

      const canvas = await html2canvas(reportContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', // 深色模式使用 gray-800，淺色模式使用白色
        windowWidth: reportContentRef.current.scrollWidth,
        windowHeight: reportContentRef.current.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 寬度 (mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pageHeight = 297; // A4 高度 (mm)

      let heightLeft = imgHeight;
      let position = 0;

      // 添加第一頁
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // 如果內容超過一頁，添加新頁
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`tax-report-${selectedPeriod}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert(t('taxReports.exportFailed'));
    }
  };

  // 如果還在載入期間列表，顯示載入中
  if (availablePeriods.length === 0 && !error) {
    return (
      <div className="container mx-auto px-10 py-6 pb-32">
        <div className="text-center text-gray-500 dark:text-gray-400">{t('common.status.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('taxReports.title')}
          </h1>
          <div className="flex items-center space-x-3">
            {/* 期間選擇器 */}
            <CustomSelect
              value={selectedPeriod}
              onChange={(value) => setSelectedPeriod(value)}
              placeholder={t('taxReports.selectPeriod')}
              className="h-10 min-w-36"
              options={availablePeriods.map(period => ({
                value: period,
                label: formatPeriodDisplay(period)
              }))}
            />

            {/* PDF 匯出按鈕 */}
            <Button
              onClick={handleExportPDF}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {t('taxReports.exportPDF')}
            </Button>
          </div>
        </div>
      </div>

      {/* 錯誤訊息 */}
      {error && (
        <div className="mb-6 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg px-4 py-3">
          <span className="text-red-800 dark:text-red-200 text-sm">{error}</span>
        </div>
      )}

      {/* 載入中狀態 */}
      {isLoading && (
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
          <div className="text-lg">{t('taxReports.loadingData')}</div>
        </div>
      )}

      {/* 稅務資料顯示 */}
      <div ref={reportContentRef}>
        {!isLoading && taxSummary && report401 && report403 && (
          <>
            {/* 稅務總覽 */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4 text-gray-700 dark:text-gray-300">
                {t('taxReports.overview')} - {formatPeriodDisplay(selectedPeriod)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* 總銷項稅額 */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 pb-2">
                    {t('taxReports.totalOutputTax')}
                  </h3>
                  <div className="text-2xl font-bold text-green-600">
                    {formatAmount(taxSummary.totalOutputTax)}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('taxReports.salesTax')}</p>
                </div>

                {/* 總進項稅額 */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 pb-2">
                    {t('taxReports.totalInputTax')}
                  </h3>
                  <div className="text-2xl font-bold text-blue-600">
                    {formatAmount(taxSummary.totalInputTax)}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('taxReports.deductibleTax')}</p>
                </div>

                {/* 淨應納稅額 */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 pb-2">
                    {t('taxReports.netTaxPayable')}
                  </h3>
                  <div className={`text-2xl font-bold ${taxSummary.isRefundable ? 'text-orange-600' : 'text-red-600'}`}>
                    {formatAmount(Math.abs(taxSummary.netTaxPayable))}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {taxSummary.isRefundable ? t('taxReports.refundableAmount') : t('taxReports.payableAmount')}
                  </p>
                </div>

                {/* 本期繳納狀態 */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 pb-2">
                    {t('taxReports.periodStatus')}
                  </h3>
                  <div className="text-2xl font-bold text-orange-600">
                    {taxSummary.isRefundable ? t('taxReports.pendingRefund') : t('taxReports.pendingFiling')}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {taxSummary.carryForwardBalance > 0 ? `${t('taxReports.carryForward')}: ${formatAmount(taxSummary.carryForwardBalance)}` : t('taxReports.noCarryForward')}
                  </p>
                </div>
              </div>
            </div>

            {/* 申報書報表區塊 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* 401 營業人銷售額與稅額申報書 */}
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('taxReports.form401Title')}
                </h3>
                <div>
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.taxableAmount')}</span>
                      <span className="font-semibold">{formatAmount(report401.taxableAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.zeroRatedAmount')}</span>
                      <span className="font-semibold">{formatAmount(report401.zeroRatedAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.taxExemptAmount')}</span>
                      <span className="font-semibold">{formatAmount(report401.taxExemptAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.outputTax')}</span>
                      <span className="font-semibold text-green-600">{formatAmount(report401.outputTax)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.totalSalesAmount')}</span>
                      <span className="font-semibold">{formatAmount(report401.totalSalesAmount)}</span>
                    </div>
                    {report401.details.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('taxReports.detailItems')}</h4>
                        <div className="space-y-2">
                          {report401.details.map((detail, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                {detail.accountCode} - {detail.accountName}
                              </span>
                              <span className="font-medium">{formatAmount(detail.taxAmount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 403 營業人進項稅額申報書 */}
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('taxReports.form403Title')}
                </h3>
                <div>
                  <div className="space-y-4">
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.inputTaxAmount')}</span>
                      <span className="font-semibold">{formatAmount(report403.inputTaxAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.nonDeductibleAmount')}</span>
                      <span className="font-semibold">{formatAmount(report403.nonDeductibleAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.deductibleAmount')}</span>
                      <span className="font-semibold">{formatAmount(report403.deductibleAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.specialDeductions')}</span>
                      <span className="font-semibold">{formatAmount(report403.specialDeductions)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200 dark:border-slate-600">
                      <span className="text-gray-600 dark:text-gray-400">{t('taxReports.totalDeductible')}</span>
                      <span className="font-semibold text-blue-600">{formatAmount(report403.totalDeductible)}</span>
                    </div>
                    {report403.details.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('taxReports.detailItems')}</h4>
                        <div className="space-y-2">
                          {report403.details.map((detail, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">
                                {detail.accountCode} - {detail.accountName}
                              </span>
                              <span className="font-medium">{formatAmount(detail.taxAmount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 稅務試算趨勢說明 */}
            <div className="mt-8">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('taxReports.calculationExplanation')}
                </h3>
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('taxReports.currentPeriodTax')}</div>
                      <div className={`text-2xl font-bold ${taxSummary.isRefundable ? 'text-orange-600' : 'text-red-600'}`}>
                        {formatAmount(Math.abs(taxSummary.netTaxPayable))}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('taxReports.carryForwardNextPeriod')}</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatAmount(taxSummary.carryForwardBalance)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('taxReports.taxStatus')}</div>
                      <div className={`text-2xl font-bold ${taxSummary.isRefundable ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        {taxSummary.isRefundable ? t('taxReports.refundable') : t('taxReports.normal')}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700 p-4 rounded-lg">
                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                      <p><strong>{t('taxReports.formula')}：</strong></p>
                      <p>{t('taxReports.formula1')}</p>
                      <p>{t('taxReports.formula2')}</p>
                      <p>{t('taxReports.formula3')}</p>
                      {taxSummary.isRefundable && (
                        <p className="text-orange-600 dark:text-orange-400 font-medium mt-2">
                          {t('taxReports.refundNote')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      {/* 無資料狀態 */}
      {!isLoading && !error && selectedPeriod && !taxSummary && (
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
          <div className="text-lg">{t('taxReports.noData')}</div>
          <div className="text-sm mt-2">{t('taxReports.noDataHint')}</div>
        </div>
      )}
    </div>
  );
};

export default TaxReportPage;
